import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes, TpMatrixColumnType } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import {
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunComponentResult,
  TpETLDryRunMatrixObject,
  isDryRunMatrixObject,
} from '../../interfaces';
import { getETLComponentInputObject, addDryRunObject } from '../../utils';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import { copyETLDryRunObjectMatrix } from '../utils';
import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import ParamSet from '../../edit-etl-component/ParamSet';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { get_guid } from '../../../visual-analytics/helpers/misc';
import { VALTYPES } from '../../../../data/interfaces';
import {
  summaryTypes,
  getSummaryTypeById,
  getSummaryTypeColumnName,
  getSummaryTypeDescription,
} from './summaryStatisticTypes';

interface TpSummaryStatistic {
  id: string;
  dimension: TpMatrixColumnType | ''; // 'observations' or 'features'
  summaryType: string; // e.g., 'mean', 'median', 'stddev'
  summaryValue?: number;
  columnIdentifier: string;
  columnName: string;
  columnDescription: string;
  columnDataType: string;
}

function getErrorsForStatistic(matrix: TpETLDryRunMatrixObject, statistic: TpSummaryStatistic): string[] {
  const errors: string[] = [];
  if (!statistic.dimension) {
    errors.push(`Dimension is required`);
  }
  if (!statistic.summaryType) {
    errors.push(`Summary statistic type is required`);
  } else {
    const summaryType = summaryTypes.find((type) => type.id === statistic.summaryType);
    if (!summaryType) {
      // Should not happen, but just in case
      errors.push(`Invalid summary type ${statistic.summaryType}`);
    } else if (summaryType.value && statistic.summaryValue == undefined) {
      errors.push(`Summary value is required`);
    }
  }

  // Identifier
  if (!statistic.columnIdentifier) {
    errors.push(`Column identifier is required`);
  }
  if (
    (statistic.dimension === 'observations' &&
      matrix.obsMetadataColumns.some((col) => col.identifier === statistic.columnIdentifier)) ||
    (statistic.dimension === 'features' &&
      matrix.featureMetadataColumns.some((col) => col.identifier === statistic.columnIdentifier))
  ) {
    errors.push(`Column identifier "${statistic.columnIdentifier}" already exists`);
  }
  return errors;
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const matrix: TpETLDryRunMatrixObject = {
    type: TpETLObjectTypes.MATRIX,
    id: comp.outputObjectIds['result'],
    createdByCompId: comp.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [],
    linkColumnId: '',
  };
  addDryRunObject(dryRun, matrix);

  const input = getETLComponentInputObject(dryRun, comp, 'source');
  if (!isDryRunMatrixObject(input)) {
    addETLDryRunError(dryRun, `Input is not a matrix`, comp.id, 'source');
    return { abort: false };
  }

  // Set the metadata columns based on the input matrix
  copyETLDryRunObjectMatrix(input, matrix);

  // Add the summary statistics columns
  const summaryStatistics = (comp.params['statistics'] || []) as TpSummaryStatistic[];
  for (const statistic of summaryStatistics) {
    const errors: string[] = getErrorsForStatistic(input, statistic);

    if (errors.length > 0) {
      errors.forEach((err) => addETLDryRunError(dryRun, err, comp.id, statistic.id));
    } else {
      const dimension = statistic.dimension;
      const value = statistic.summaryValue;
      const columnType = dimension === 'observations' ? matrix.obsMetadataColumns : matrix.featureMetadataColumns;
      columnType.push({
        identifier: statistic.columnIdentifier,
        name: statistic.columnName || getSummaryTypeColumnName(statistic.summaryType, dimension, value),
        description: statistic.columnDescription || getSummaryTypeDescription(statistic.summaryType, dimension, value),
        dataType: statistic.columnDataType,
      });
    }
  }

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const summaryStatistics = (comp.params['statistics'] || []) as TpSummaryStatistic[];

  function updateStatistic(statisticId: string, paramId: string, newValue: any) {
    const statistic = summaryStatistics.find((stat) => stat.id === statisticId);
    if (statistic === undefined) throw `Invalid summary statistic id ${statisticId}`;
    else if (paramId == 'dimension') statistic.dimension = newValue;
    else if (paramId == 'summaryType') statistic.summaryType = newValue;
    else if (paramId == 'summaryValue') statistic.summaryValue = newValue;
    else if (paramId == 'columnIdentifier') statistic.columnIdentifier = newValue;
    else if (paramId == 'columnName') statistic.columnName = newValue;
    else if (paramId == 'columnDescription') statistic.columnDescription = newValue;
    updateEditingComponent(comp);
  }

  function renderErrors(paramSet: TpSummaryStatistic) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == paramSet.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${paramSet.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  function removeStatistic(statisticId: string) {
    comp.params['statistics'] = summaryStatistics.filter((stat) => stat.id !== statisticId);
    updateEditingComponent(comp);
  }

  function addNewStatistic() {
    const newStatistic: TpSummaryStatistic = {
      id: get_guid(),
      dimension: '',
      summaryType: '',
      summaryValue: 0,
      columnIdentifier: '',
      columnName: '',
      columnDescription: '',
      columnDataType: VALTYPES.NUMBER,
    };
    comp.params['statistics'] = [...summaryStatistics, newStatistic];
    updateEditingComponent(comp);
  }

  return (
    <>
      {summaryStatistics.map((statistic) => (
        <div className={styles.section} key={statistic.id}>
          <h3>
            Summary statistic
            <button onClick={() => removeStatistic(statistic.id)}>
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          <ParamSet
            params={[
              {
                name: 'Matrix dimension',
                component: (
                  <>
                    <select
                      value={statistic.dimension}
                      onChange={(ev) => updateStatistic(statistic.id, 'dimension', ev.target.value)}
                    >
                      <option value={''}> - Select - </option>
                      <option value={'observations'}> Observations (rows) </option>
                      <option value={'features'}> Features (columns) </option>
                    </select>
                    <div style={{ marginTop: '3px' }}>
                      Calculate the value over the rows (observations) or columns (features) of the matrix.
                    </div>
                  </>
                ),
              },
              {
                name: 'Summary statistic type',
                component: (
                  <>
                    <select
                      value={statistic.summaryType}
                      onChange={(ev) => updateStatistic(statistic.id, 'summaryType', ev.target.value)}
                    >
                      <option key="_empty_" value={''}>
                        - Select -
                      </option>
                      {summaryTypes.map((summaryType) => (
                        <option key={summaryType.id} value={summaryType.id}>
                          {summaryType.name}
                        </option>
                      ))}
                    </select>
                    {statistic.summaryType && summaryTypes.find((type) => type.id === statistic.summaryType)?.value && (
                      <input
                        type="number"
                        value={statistic.summaryValue}
                        style={{ marginLeft: '16px' }}
                        onChange={(ev) => updateStatistic(statistic.id, 'summaryValue', parseFloat(ev.target.value))}
                      />
                    )}
                    <div style={{ marginTop: '3px' }}>
                      {getSummaryTypeById(statistic.summaryType)
                        ? getSummaryTypeDescription(statistic.summaryType, '')
                        : 'Select the type of summary statistic to calculate. If the statistic requires a value, you can specify it here.'}
                    </div>
                  </>
                ),
              },
              {
                name: 'Column identifier',
                component: (
                  <input
                    value={statistic.columnIdentifier}
                    onChange={(ev) => updateStatistic(statistic.id, 'columnIdentifier', ev.target.value)}
                  />
                ),
              },
              {
                name: 'Column name',
                component: (
                  <input
                    value={statistic.columnName}
                    placeholder="Automatic"
                    onChange={(ev) => updateStatistic(statistic.id, 'columnName', ev.target.value)}
                  />
                ),
              },
              {
                name: 'Column description',
                component: (
                  <input
                    value={statistic.columnDescription}
                    placeholder="Automatic"
                    onChange={(ev) => updateStatistic(statistic.id, 'columnDescription', ev.target.value)}
                  />
                ),
              },
            ]}
          />
          {renderErrors(statistic)}
        </div>
      ))}

      <FancyButton iconName="fas:plus" onClick={addNewStatistic}>
        Add summary statistic
      </FancyButton>
    </>
  );
}

export const manifestETLApplySummaryStatMatrix: TpETLComponentTypeManifest = {
  id: 'ApplySummaryStatMatrix',
  name: 'Add summary statistics',
  description: 'Add summary statistics to observations or features.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['fas', 'function']} />,
  performDryRun,
  inputs: [
    {
      id: 'source',
      name: 'Source matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Input matrix.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.MATRIX,
      id: 'result',
      name: 'Result matrix',
      description: 'Matrix with summary statistics.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [],
  renderCustomConfigParams,
};
